import dayjs from 'dayjs';
import { getCalendarOwnerIds } from '../../../../utils/calendarOwners';
import { getCalendarMemberIds } from '../../../../utils/calendarMembers';

export const TIME_GRID_SLOT_MINUTES = 30;
const SYSTEM_TASK_TYPE = Object.freeze({
  MEMBER_BIRTHDAY: 1,
  PARTNER_BIRTHDAY: 2,
});
const BIRTHDAY_SYSTEM_TASK_TYPES = new Set(Object.values(SYSTEM_TASK_TYPE));

export function getCategoryNumber(event) {
  const raw = event?.category_id ?? event?.categoryId ?? event?.category ?? event?.cat ?? 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function isSystemCreated(event) {
  const value = event?.created_by_id ?? event?.created_by ?? event?.createdBy ?? null;
  return String(value ?? '').toUpperCase() === 'SYSTEM';
}

export function isBirthdayEvent(event) {
  return BIRTHDAY_SYSTEM_TASK_TYPES.has(Number(event?.system_task_type));
}

export function isCompletedRaw(event) {
  return event?.completed === true || event?.completed === 1 || event?.completed === '1';
}

export function getEventColorClasses(event) {
  return String(event?.color ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

export function toHHmm(timeRaw) {
  if (!timeRaw || typeof timeRaw !== 'string') return '';

  const parts = timeRaw.split(':');
  if (parts.length < 2) return '';

  const hours = String(parts[0]).padStart(2, '0');
  const minutes = String(parts[1]).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function mapActiveCategories(rows) {
  return rows
    .filter((category) => Number(category?.active) === 1)
    .map((category) => ({
      id: Number(category.id),
      label: String(category.category ?? '').trim(),
    }))
    .filter((category) => Number.isFinite(category.id) && category.label);
}

export function mergeCheckedCategories(previousChecks, categories) {
  const next = { ...(previousChecks || {}) };

  categories.forEach((category) => {
    if (next[category.id] === undefined) next[category.id] = true;
  });

  return next;
}

export function filterVisibleEvents(events, filters) {
  return (Array.isArray(events) ? events : []).filter((event) => isEventVisible(event, filters));
}

export function getAssignedMemberIds(event) {
  return getCalendarMemberIds(event);
}

export function isEventVisible(event, { onlyWithHacs, showSystemTasks, catChecks, advancedFilters }) {
  const assignedMemberIds = getAssignedMemberIds(event);
  if (onlyWithHacs && event?.fromMyHacs !== true && assignedMemberIds.length === 0) return false;
  if (!showSystemTasks && isSystemCreated(event)) return false;

  const isCompleted = isCompletedRaw(event);
  if (advancedFilters?.completedOnly ? !isCompleted : isCompleted) return false;
  if (advancedFilters?.pmaIds?.length) {
    const ownerIds = getCalendarOwnerIds(event);
    if (!advancedFilters.pmaIds.some((ownerId) => ownerIds.includes(String(ownerId)))) return false;
  }
  if (advancedFilters?.memberIds?.length) {
    if (!advancedFilters.memberIds.some((id) => assignedMemberIds.includes(String(id)))) return false;
  }

  const categoryNumber = getCategoryNumber(event);
  return Boolean(catChecks?.[categoryNumber]);
}

export function shouldShowMiniCalendarBadge(event) {
  return isBirthdayEvent(event) || !isCompletedRaw(event);
}

export function getDateInTargetMonth(currentDate, targetMonth) {
  const current = dayjs(currentDate);
  const target = dayjs(targetMonth);
  if (!target.isValid()) return null;

  const preferredDay = current.isValid() ? current.date() : 1;
  const day = Math.min(preferredDay, target.daysInMonth());
  return target.startOf('month').date(day);
}

export function buildMiniCalendarBadgeDays(events, { monthStart, monthEnd }) {
  const daysSet = new Set();

  (Array.isArray(events) ? events : []).forEach((event) => {
    if (!shouldShowMiniCalendarBadge(event)) return;

    const startRaw = event.starting_date ?? event.startDate ?? event.date ?? null;
    const endRaw = event.ending_date ?? event.endDate ?? startRaw ?? null;
    if (!startRaw) return;

    const start = dayjs(startRaw).startOf('day');
    const end = dayjs(endRaw).startOf('day');
    if (!start.isValid() || !end.isValid()) return;

    const rangeStart = start.isAfter(monthStart, 'day') ? start : monthStart.startOf('day');
    const rangeEnd = end.isBefore(monthEnd, 'day') ? end : monthEnd.startOf('day');
    if (rangeEnd.isBefore(rangeStart)) return;

    for (let current = rangeStart; current.isSame(rangeEnd, 'day') || current.isBefore(rangeEnd, 'day'); current = current.add(1, 'day')) {
      daysSet.add(current.date());
    }
  });

  return Array.from(daysSet).sort((firstDay, secondDay) => firstDay - secondDay);
}

export function rawToFullCalendarEvents(raw, range = {}) {
  const startDateRaw = raw?.starting_date ?? raw?.startDate ?? raw?.date ?? null;
  if (!startDateRaw) return [];

  const allDay = Boolean(raw?.all_day ?? raw?.allDay);
  const categoryNumber = getCategoryNumber(raw);
  const completedClass = isCompletedRaw(raw) ? 'completed' : '';
  const colorClasses = getEventColorClasses(raw);

  const startDate = dayjs(startDateRaw);
  if (!startDate.isValid()) return [];

  if (allDay) {
    const endDateRaw = raw?.ending_date ?? raw?.endDate ?? startDateRaw;
    const endDate = dayjs(endDateRaw);
    if (!endDate.isValid() || endDate.isBefore(startDate, 'day')) return [];

    const rangeStart = range.start ? dayjs(range.start) : null;
    const rangeEnd = range.end ? dayjs(range.end) : null;
    const firstOccurrence = rangeStart?.isValid() && rangeStart.isAfter(startDate, 'day')
      ? rangeStart.startOf('day')
      : startDate.startOf('day');
    const lastOccurrence = rangeEnd?.isValid() && rangeEnd.isBefore(endDate, 'day')
      ? rangeEnd.startOf('day')
      : endDate.startOf('day');

    if (lastOccurrence.isBefore(firstOccurrence, 'day')) return [];

    const occurrences = [];
    for (
      let occurrenceDate = firstOccurrence;
      occurrenceDate.isSame(lastOccurrence, 'day') || occurrenceDate.isBefore(lastOccurrence, 'day');
      occurrenceDate = occurrenceDate.add(1, 'day')
    ) {
      const occurrenceKey = occurrenceDate.format('YYYY-MM-DD');
      occurrences.push({
        id: `${String(raw?.id ?? '')}--${occurrenceKey}`,
        title: String(raw?.title ?? raw?.text ?? ''),
        start: occurrenceDate.toDate(),
        end: occurrenceDate.add(1, 'day').toDate(),
        allDay: true,
        classNames: ['fc-task', ...colorClasses, `cat-${categoryNumber}`, completedClass].filter(Boolean),
        extendedProps: { raw, occurrenceDate: occurrenceKey },
      });
    }

    return occurrences;
  }

  const startTime = toHHmm(raw?.starting_time ?? raw?.time ?? '');
  const startDateTime = startTime
    ? dayjs(`${startDate.format('YYYY-MM-DD')}T${startTime}:00`)
    : startDate.startOf('day');

  if (!startDateTime.isValid()) return [];

  return [{
    id: String(raw?.id ?? ''),
    title: String(raw?.title ?? raw?.text ?? ''),
    start: startDateTime.toDate(),
    end: startDateTime.add(TIME_GRID_SLOT_MINUTES, 'minute').toDate(),
    allDay: false,
    classNames: ['fc-task', ...colorClasses, `cat-${categoryNumber}`, completedClass].filter(Boolean),
    extendedProps: { raw },
  }];
}

export function stackTimeGridEvents(events) {
  const passthroughEvents = [];
  const timeSlotGroups = new Map();

  (Array.isArray(events) ? events : []).forEach((event) => {
    if (event?.allDay || !event?.start) {
      passthroughEvents.push(event);
      return;
    }

    const eventStart = dayjs(event.start);
    if (!eventStart.isValid()) {
      passthroughEvents.push(event);
      return;
    }

    const slotStart = eventStart
      .second(0)
      .millisecond(0)
      .minute(eventStart.minute() < 30 ? 0 : 30);
    const slotKey = slotStart.format('YYYY-MM-DDTHH:mm');
    const currentGroup = timeSlotGroups.get(slotKey) ?? {
      slotStart,
      events: [],
    };

    currentGroup.events.push(event);
    timeSlotGroups.set(slotKey, currentGroup);
  });

  const stackedEvents = Array.from(timeSlotGroups.entries()).map(([slotKey, group]) => {
    const orderedEvents = [...group.events].sort((firstEvent, secondEvent) => {
      const startDifference = dayjs(firstEvent.start).valueOf() - dayjs(secondEvent.start).valueOf();
      if (startDifference !== 0) return startDifference;
      return String(firstEvent.title ?? '').localeCompare(String(secondEvent.title ?? ''));
    });

    return {
      id: `time-slot-${slotKey}`,
      start: group.slotStart.toDate(),
      end: group.slotStart.add(TIME_GRID_SLOT_MINUTES, 'minute').toDate(),
      allDay: false,
      classNames: ['fc-time-slot-stack'],
      extendedProps: { timeSlotEvents: orderedEvents },
    };
  });

  return [...passthroughEvents, ...stackedEvents];
}
